import React, { useMemo, useEffect, useState } from 'react'
import { View, FlatList } from 'react-native'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import StopsCard from './StopsCard'
import LoadDetail from '../LoadDetail'
import StopLocation from './StopLocation'
import Button from '../../../Button'
import Txt from '../../../Txt'
import { useTheme } from '../../../../theming/ThemeProvider'
import useStyles from './useStyles'
import { initSelector } from '../../../../redux/initReducer/initSelector'
import MapComponent from '../../../Map'
import { usePlatforms } from '../../../../hooks/usePlatforms'

const LoadDescription = ({ module, load, onBid, onBook, showMap, tractorDetails }) => {
  const { theme } = useTheme()
  const { isTablet } = usePlatforms()
  const styles = useStyles(theme, isTablet)
  const { equipmentTypes } = useSelector(initSelector)
  const loadEquipment = equipmentTypes.find((type) => type.equipment_code === load?.equipmentType)

  const showStopsCard = useMemo(() => module !== 'ready-to-invoice', [module])

  const showStopsLocations = useMemo(() =>
    module === 'assigned-orders' || module === 'ready-to-invoice', [module])

  const showButtons = useMemo(() => load?.order_status !== 'BOOK' && (module === 'my-loads' ||
      module === 'all-loads' || module === 'my-offers'), [load.order_status, module])

  const [loadRoute, setLoadRoute] = useState()

  useEffect(() => {
    if (showMap) {
      setLoadRoute(load && load.origin?.coordinates && load.destination?.coordinates
        ? [
          load.origin.coordinates,
          ...load.stops.map(stop => stop.coordinates),
          load.destination.coordinates
        ] : null)
    }
  }, [load, showMap])

  return (
    <>
      {Boolean(showMap) && (
        <View style={styles.mapContainer}>
          <MapComponent
            routes={loadRoute ? [loadRoute] : []}
            tractorPosition={
              tractorDetails && tractorDetails.last_location &&
                tractorDetails.last_location.latitude && tractorDetails.last_location.longitude
                ? tractorDetails.last_location : null
            }
            width={400}
            height={300}
            mapId='detailsMap'
          />
        </View>
      )}
      <View style={styles.container}>
        {showStopsCard && <StopsCard load={load} />}
        {showButtons && (
          <View style={styles.buttonsContainer}>
            <View style={styles.bidButtonContainer}>
              <Button
                title='Bid'
                onPress={onBid}
                style={styles.bidButton}
              />
            </View>
            {load?.rate && (
              <View style={styles.bookButtonContainer}>
                <Button
                  title='Book'
                  onPress={onBook}
                  style={styles.bookButton}
                />
              </View>
            )}
          </View>
        )}
        <View style={styles.detailsContainer}>
          <View style={styles.detailsRow}>
            <LoadDetail
              icon='Tractor2'
              title='Trailer Type'
              text={loadEquipment?.description || '- -'}
            />
            <LoadDetail
              icon='Rate'
              title='Rate'
              text={load?.rate ? `$${load.rate} USD` : '- -'}
            />
          </View>
          <View style={styles.detailsRow}>
            <LoadDetail
              icon='Lbs'
              title='Weight'
              text={load?.weight ? `${load.weight} Lbs` : '- -'}
            />
            <LoadDetail
              icon='Rule'
              title='Distance'
              text={load?.distance ? `${load.distance} mi` : '- -'}
            />
          </View>
        </View>
        {showStopsLocations && (
          <View style={styles.detailsContainer}>
            <StopLocation prefix='Pick Up' stop={load?.origin} />
            {Boolean(load?.stops && load?.stops.length) && (
              <FlatList
                data={load?.stops || []}
                renderItem={({ item, index }) => (
                  <StopLocation
                    prefix={`Stop ${index + 1}`}
                    stop={item}
                  />
                )}
                keyExtractor={(_, key) => key.toString()}
              />
            )}
            <StopLocation prefix='Drop Off' stop={load?.destination} />
          </View>
        )}
        <View style={styles.commodityContainer}>
          <Txt
            text='Commodity'
            identity='body2'
            color='SLATE_GRAY'
          />
          <View style={styles.commodity}>
            <Txt
              text={load?.commodity}
              identity='body3'
              color='OUTER_SPACE'
              selectable
            />
          </View>
        </View>
        <View style={styles.commentsContainer}>
          <Txt
            text='Comments'
            identity='body2'
            color='SLATE_GRAY'
          />
          <View style={styles.comments}>
            <Txt
              text={load?.comments?.replace(/\\n/g, '\n')}
              identity='body3'
              color='OUTER_SPACE'
              selectable
            />
          </View>
        </View>
      </View>
    </>
  )
}

LoadDescription.propTypes = {
  load: PropTypes.object,
  module: PropTypes.string,
  onBid: PropTypes.func,
  onBook: PropTypes.func,
  showMap: PropTypes.bool,
  tractorDetails: PropTypes.object
}

export default LoadDescription
